import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomizeLinksModal from './CustomizeLinksModal';
import { sectionsData } from './sectionsData';
import { useAuth } from '../../context/AuthContext';

const PREFERENCES_URL = 'https://api.axillonaerospace.info/api/user-preferences/';

// Create a reusable LinkButton component
const LinkButton = ({ color, icon, label, url, requiresVpn, description }) => {
  return (
    <Tooltip title={description || ''} arrow placement="top">
      <Button
        variant="contained"
        color={requiresVpn ? 'secondary' : color}
        startIcon={<FontAwesomeIcon icon={icon}/>}
        aria-label={label}
        onClick={() => window.open(url, '_blank')}
        sx={{ padding: 2, minWidth: 200 }}
      >
        {label}
      </Button>
    </Tooltip>
  );
};

const Links = () => {
  const { apiFetch } = useAuth();
  const [userSelections, setUserSelections] = useState({});
  const [showModal, setShowModal] = useState(false);

  // Fetch user preferences from the backend
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await apiFetch(PREFERENCES_URL);
        const data = await response.json();
        setUserSelections(data.sections || {});
      } catch (error) {
        console.error('Error fetching user preferences:', error);
      }
    };
    fetchPreferences();
  }, [apiFetch]);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  // Save toggles to Django
  const handleSave = async (newSelections) => {
    try {
      const response = await apiFetch(PREFERENCES_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: newSelections }),
      });
      const data = await response.json();
      console.log('Updated preferences:', data);
      setUserSelections(data.sections || {});
    } catch (err) {
      console.error('Error updating preferences:', err);
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url(/white.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundColor: 'rgba(255, 255, 255, 0.91)',
        backgroundBlendMode: 'overlay',
        padding: 2,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
        },
      }}
    >
      {/* Manage Sections Button */}
      <Box sx={{ textAlign: 'right' }}>
        <Button onClick={handleOpenModal} sx={{ fontSize: '1.15rem' }}>
          Manage Links
        </Button>
      </Box>

      {/* Render sections based on user preferences */}
      {sectionsData
        .filter((section) => userSelections[section.title] !== false)
        .map((section, index) => (
          <Box
            key={section.title}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 1, md: 3 },
              py: 2,
              borderTop: index === 0 ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                width: { xs: 'auto', md: 180 },
                flexShrink: 0,
                textAlign: { xs: 'center', md: 'right' },
                pt: { md: 1 },
              }}
            >
              {section.title}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-start' },
                flex: 1,
              }}
            >
              {section.links.map((link) => (
                <LinkButton
                  key={link.label}
                  color={section.color}
                  icon={link.icon}
                  label={link.label}
                  url={link.url}
                  requiresVpn={link.requiresVpn}
                  description={link.description}
                />
              ))}
            </Box>
          </Box>
        ))}

      {/* Legend/Key */}
      <Box sx={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: 'secondary.main' }} />
          <Typography variant="body2">Require Zscaler On</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, backgroundColor: 'primary.main' }} />
          <Typography variant="body2">No Zscaler Required</Typography>
        </Box>
      </Box>

      {/* Modal for toggles */}
      <CustomizeLinksModal
        open={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        sectionNames={sectionsData.map((s) => s.title)}
        defaultSelections={userSelections}
      />
    </Box>
  );
};

export default Links;